import {Popover} from "@mantine/core";
import type {DatesRangeValue} from "@mantine/dates";
import {DatePicker} from "@mantine/dates";
import {DateTime} from "luxon";
import {ChevronRight, Link45deg} from "react-bootstrap-icons";
import {twMerge} from "tailwind-merge";
import {graphColors} from "~/constants/graphColors";
import {useIsFullScreen} from "~/hooks/useIsFullScreen";
import type {EditableReportState} from "~/modules/reports/state/editableReportState";
import type {CanvasControls} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/reportsWorkspaceCapability/domain/ReportJson";
import {
    DateRangeOptions,
    WidgetValueType,
} from "~/modules/reports/domain/reportTypes";
import {getDatesForDateRangeOption} from "~/modules/reports/utilities/reportUtilities";
import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {ItemBuilder} from "@vesta/global-common-typescript/components/itemBuilder";
import ArrowDownIcon from "~/svgs/arrowDownDropdown.svg?react";
import ArrowDownFill from "~/svgs/arrowDownFill.svg?react";
import type {EnumDictionary} from "~/typeDefinitions";

export enum DateRangeCategories {
    popular = "08e56dbb-b9d7-4b03-a1ce-3d24f698b305",
    current = "07818308-0878-44cb-b9ce-d52d17c80cdb",
    previous = "6f2160ea-616a-4b11-86b3-c03372565c99",
    custom = "f7cdcba1-aed0-4975-b5c0-1267d362f15a",
}

export const dateRangeCategoriesMetadata: EnumDictionary<DateRangeCategories, {humanReadableName: string; hasSubOptions: boolean}> = {
    [DateRangeCategories.popular]: {
        hasSubOptions: true,
        humanReadableName: "Popular",
    },
    [DateRangeCategories.current]: {
        hasSubOptions: true,
        humanReadableName: "Current",
    },
    [DateRangeCategories.previous]: {
        hasSubOptions: true,
        humanReadableName: "Previous",
    },
    [DateRangeCategories.custom]: {
        hasSubOptions: false,
        humanReadableName: "Custom",
    },
};

export type DateRangeOption = {
    id: DateRangeOptions;
    humanReadableName: string;
    categories: Array<DateRangeCategories>;
};

export const dateRangeOptions: EnumDictionary<DateRangeOptions, DateRangeOption> = {
    [DateRangeOptions.today]: {
        id: DateRangeOptions.today,
        humanReadableName: "Today",
        categories: [DateRangeCategories.current],
    },
    [DateRangeOptions.yesterday]: {
        id: DateRangeOptions.yesterday,
        humanReadableName: "Yesterday",
        categories: [DateRangeCategories.previous],
    },
    [DateRangeOptions.thisWeek]: {
        id: DateRangeOptions.thisWeek,
        humanReadableName: "This week",
        categories: [DateRangeCategories.current],
    },
    [DateRangeOptions.lastWeek]: {
        id: DateRangeOptions.lastWeek,
        humanReadableName: "Last week",
        categories: [],
    },
    [DateRangeOptions.lastSevenDays]: {
        id: DateRangeOptions.lastSevenDays,
        humanReadableName: "Last 7 days",
        categories: [],
    },
    [DateRangeOptions.lastFourteenDays]: {
        id: DateRangeOptions.lastFourteenDays,
        humanReadableName: "Last 14 days",
        categories: [],
    },
    [DateRangeOptions.monthToDate]: {
        id: DateRangeOptions.monthToDate,
        humanReadableName: "This month",
        categories: [DateRangeCategories.popular],
    },
    [DateRangeOptions.lastMonth]: {
        id: DateRangeOptions.lastMonth,
        humanReadableName: "Last month",
        categories: [DateRangeCategories.popular, DateRangeCategories.previous],
    },
    [DateRangeOptions.lastMonthToDate]: {
        id: DateRangeOptions.lastMonthToDate,
        humanReadableName: "Last month to date",
        categories: [DateRangeCategories.previous],
    },
    [DateRangeOptions.lastThirtyDays]: {
        id: DateRangeOptions.lastThirtyDays,
        humanReadableName: "Last 30 days",
        categories: [],
    },
    [DateRangeOptions.quarterToDate]: {
        id: DateRangeOptions.quarterToDate,
        humanReadableName: "Quarter to date",
        categories: [],
    },
    [DateRangeOptions.lastQuarter]: {
        id: DateRangeOptions.lastQuarter,
        humanReadableName: "Last quarter",
        categories: [],
    },
    [DateRangeOptions.lastQuarterToDate]: {
        id: DateRangeOptions.lastQuarterToDate,
        humanReadableName: "Last quarter to date",
        categories: [],
    },
    [DateRangeOptions.yearToDate]: {
        id: DateRangeOptions.yearToDate,
        humanReadableName: "Year to date",
        categories: [DateRangeCategories.popular],
    },
    [DateRangeOptions.lastYear]: {
        id: DateRangeOptions.lastYear,
        humanReadableName: "Last year",
        categories: [DateRangeCategories.previous],
    },
    [DateRangeOptions.lastYearToDate]: {
        id: DateRangeOptions.lastYearToDate,
        humanReadableName: "Last year to date",
        categories: [DateRangeCategories.previous],
    },
};

export function DateRangePicker({
    values,
    onCalendarDatesChangeFunction,
    onSubOptionClickFunction,
    onCloseFunction,
    targetClassName,
    existingDateRangeOption,
    canvasControls,
    isDateRangeEditable,
    timezone,
}: {
    values: [Date | null, Date | null];
    onCalendarDatesChangeFunction: (values: DatesRangeValue) => void;
    onSubOptionClickFunction: (option: DateRangeOptions | Uuid) => void;
    onCloseFunction?: () => void;
    targetClassName?: string;
    existingDateRangeOption?: DateRangeOptions | Uuid | null;
    canvasControls: CanvasControls | null;
    isDateRangeEditable?: boolean;
    timezone: string;
}) {
    const isFullScreen = useIsFullScreen();

    return (
        <>
            <Popover
                position="bottom-end"
                onClose={onCloseFunction}
                classNames={{
                    arrow: "!bg-ds-surface !border-t-sg-ts-delayed-stroke-primary !border-l-sg-ts-delayed-stroke-primary",
                }}
                disabled={isDateRangeEditable === false}
            >
                <Popover.Target>
                    <button
                        type="button"
                        className={twMerge(
                            "place-items-center mx-auto transition duration-150 grid group-hover:visible",
                            targetClassName,
                            isDateRangeEditable === false ? "cursor-default" : "cursor-pointer",
                            canvasControls != null && canvasControls[existingDateRangeOption] != null ? "invisible" : "",
                        )}
                    >
                        <div className="grid-flow-col auto-cols-max gap-x-1 items-center grid">
                            {canvasControls != null && canvasControls[existingDateRangeOption] != null && (
                                <Link45deg
                                    className="h-3 w-3"
                                    style={{
                                        color:
                                            canvasControls != null && canvasControls[existingDateRangeOption] != null
                                                ? graphColors[
                                                      Object.values(canvasControls).findIndex(
                                                          (control) => control.id === canvasControls[existingDateRangeOption].id,
                                                      ) % graphColors.length
                                                  ]
                                                : "none",
                                    }}
                                />
                            )}
                            <div className="gj-text-body-300 text-[0.625rem] text-left">
                                {getDatePickerLabelWithCanvasControls(canvasControls, existingDateRangeOption, values, timezone)}
                            </div>
                            {isFullScreen === false && isDateRangeEditable !== false && (
                                <div>
                                    <ArrowDownFill className="h-2 w-2 fill-ds-icon" />
                                </div>
                            )}
                        </div>
                    </button>
                </Popover.Target>
                <Popover.Dropdown
                    classNames={{
                        dropdown: "!p-0 !border-ds-border !border-1 !rounded-sg-small",
                    }}
                >
                    <DatePickerDropdownPanel
                        onSubOptionClickFunction={onSubOptionClickFunction}
                        onCalendarDatesChangeFunction={onCalendarDatesChangeFunction}
                        values={values}
                        existingDateRangeOption={existingDateRangeOption}
                        canvasControls={canvasControls}
                    />
                </Popover.Dropdown>
            </Popover>
        </>
    );
}

export function RightPaneDateRangePicker({
    values,
    onCalendarDatesChangeFunction,
    onSubOptionClickFunction,
    targetClassName,
    existingDateRangeOption,
    canvasControls,
    isDateRangeEditable,
    editableReportState,
}: {
    values: [Date | null, Date | null];
    onCalendarDatesChangeFunction: (values: DatesRangeValue) => void;
    onSubOptionClickFunction: (option: DateRangeOptions | Uuid) => void;
    targetClassName?: string;
    existingDateRangeOption?: DateRangeOptions | Uuid | null;
    canvasControls: CanvasControls | null;
    isDateRangeEditable?: boolean;
    editableReportState: EditableReportState;
}) {
    const isFullScreen = useIsFullScreen();

    return (
        <>
            <Popover
                position="bottom-end"
                classNames={{
                    arrow: "!bg-ds-surface !border-t-ds-border !border-l-ds-border",
                }}
                disabled={isDateRangeEditable === false}
            >
                <Popover.Target>
                    <button
                        type="button"
                        className={twMerge(
                            "grid place-items-center mx-auto w-full rounded-md border border-solid border-ds-border bg-transparent py-2 pl-3 pr-[0.6875rem] text-ds-text !h-9 transition duration-200 hover:bg-ds-surface-hovered",
                            targetClassName,
                            isDateRangeEditable === false ? "cursor-default" : "cursor-pointer",
                        )}
                    >
                        <div className="w-full grid grid-flow-col auto-cols-max items-center justify-between">
                            <div className="gj-text-label-400 text-left">
                                {getDatePickerLabelWithCanvasControls(canvasControls, existingDateRangeOption, values, editableReportState.timezone)}
                            </div>
                            {isFullScreen === false && isDateRangeEditable !== false && (
                                <div>
                                    <ArrowDownIcon className="text-ds-text-subtle h-3 w-3 fill-ds-icon" />
                                </div>
                            )}
                        </div>
                    </button>
                </Popover.Target>
                <Popover.Dropdown
                    classNames={{
                        dropdown: `!p-0 !border-ds-border !border-1 !rounded-sg-small date_range_picker`,
                    }}
                    id="dateRangePopover"
                >
                    <DatePickerDropdownPanel
                        onSubOptionClickFunction={onSubOptionClickFunction}
                        onCalendarDatesChangeFunction={onCalendarDatesChangeFunction}
                        values={values}
                        existingDateRangeOption={existingDateRangeOption}
                        canvasControls={canvasControls}
                    />
                </Popover.Dropdown>
            </Popover>
        </>
    );
}

export function DatePickerDropdownPanel({
    onSubOptionClickFunction,
    onCalendarDatesChangeFunction,
    existingDateRangeOption,
    values,
    canvasControls,
}: {
    onSubOptionClickFunction: (option: DateRangeOptions | Uuid) => void;
    onCalendarDatesChangeFunction: (values: DatesRangeValue) => void;
    existingDateRangeOption?: DateRangeOptions | Uuid | null;
    values: [Date | null, Date | null];
    canvasControls: CanvasControls | null;
}) {
    return (
        // Entire panel
        <div className="grid grid-flow-col auto-cols-max bg-ds-surface">
            {/* Left panel */}
            {/* Force this to take the height of its parent, with a fixed width */}
            <div className="w-48 h-full relative">
                <div className="w-full h-full absolute inset-0 grid grid-flow-row auto-rows-max overflow-y-auto overflow-x-clip py-[0.375rem] bg-ds-surface">
                    <ItemBuilder
                        items={[
                            ...Object.entries(dateRangeOptions).map(([option, metadata]) => ({
                                id: option as DateRangeOptions,
                                metadata: metadata,
                                type: WidgetValueType.intrinsic,
                            })),
                            // ...(canvasControls == null
                            //     ? []
                            //     : Object.values(canvasControls)
                            //           .filter(
                            //               (canvasControl) =>
                            //                   canvasControl.type ===
                            //                   CanvasControlType.dateRange,
                            //           )
                            //           .map((canvasControl) => ({
                            //               id: canvasControl.id,
                            //               metadata: {
                            //                   humanReadableName: canvasControl.name,
                            //               },
                            //               type: WidgetValueType.canvasControl,
                            //           }))),
                        ]}
                        itemBuilder={(option) => {
                            return (
                                <DatePickerTextTile
                                    displayText={option.metadata.humanReadableName}
                                    disableChevron={true}
                                    onClick={() => {
                                        onSubOptionClickFunction(option.id);
                                    }}
                                    className={existingDateRangeOption === option.id ? "bg-ds-surface-pressed" : ""}
                                    key={option.id}
                                />
                            );
                        }}
                    />
                </div>
            </div>

            {/* Right panel */}
            <div className="grid grid-flow-row auto-rows-max gap-y-4 p-4 bg-ds-surface">
                <DatePicker
                    type="range"
                    value={values}
                    onChange={onCalendarDatesChangeFunction}
                    excludeDate={(currentDate) => {
                        const jsDate = new Date(currentDate);
                        return DateTime.fromJSDate(jsDate).startOf("day") > DateTime.now().startOf("day");
                    }}
                    allowSingleDateInRange
                    classNames={{
                        monthCell: "!p-0",
                        day: "normalise-weekend-dates",
                    }}
                    // className="gj-text-body-300 bg-ds-surface-raised "
                    className="gj-text-body-300 bg-ds-surface"
                />
            </div>
        </div>
    );
}

export function DatePickerTextTile({
    displayText,
    disableChevron,
    CustomChevron,
    className,
    onClick,
}: {
    displayText: string;
    disableChevron?: boolean | null;
    CustomChevron?: React.ComponentType<{className: string}>;
    className?: string;
    onClick: () => void;
}) {
    return (
        <button
            className={twMerge(
                "px-4 min-w-48 py-[0.625rem] transition duration-200 bg-ds-surface hover:bg-ds-surface-hovered grid grid-flow-col auto-cols-max justify-between items-center relative cursor-pointer",
                className,
            )}
            onClick={onClick}
            type="button"
        >
            <div className="gj-text-label-400">{displayText}</div>
            {disableChevron !== true &&
                (CustomChevron == null ? (
                    <div>
                        <ChevronRight className="h-4 w-4 fill-ds-icon" />
                    </div>
                ) : (
                    <div>
                        <CustomChevron className="h-4 w-4 fill-ds-icon" />
                    </div>
                ))}
        </button>
    );
}

export function getDatePickerLabelWithCanvasControls(
    canvasControls: CanvasControls | null,
    existingDateRangeOption: DateRangeOptions | Uuid | null,
    values: [Date | null, Date | null],
    timezone: string,
) {
    if (existingDateRangeOption == null && values[0] != null && values[1] != null) {
        return `${DateTime.fromJSDate(values[0]).toFormat("dd LLL yyyy")} - ${DateTime.fromJSDate(values[1]).toFormat("dd LLL yyyy")}`;
    } else if (existingDateRangeOption != null && dateRangeOptions[existingDateRangeOption] != null) {
        return dateRangeOptions[existingDateRangeOption].humanReadableName;
    } else if (canvasControls != null && canvasControls[existingDateRangeOption] != null && canvasControls[existingDateRangeOption].value != null) {
        if (
            canvasControls[existingDateRangeOption].dateRange == null &&
            canvasControls[existingDateRangeOption].value[0] != null &&
            canvasControls[existingDateRangeOption].value[1] != null
        ) {
            const canvasControl = canvasControls[existingDateRangeOption];
            const startDateParsed = DateTime.fromISO(canvasControl.value[0]).toFormat("dd LLL yyyy");
            const endDateParsed = DateTime.fromISO(canvasControl.value[1]).toFormat("dd LLL yyyy");

            return `${startDateParsed} - ${endDateParsed}`;
        }

        const {startDate, endDate} = getDatesForDateRangeOption(canvasControls[existingDateRangeOption].dateRange, timezone);

        const startDateParsed = DateTime.fromISO(startDate).toFormat("dd LLL yyyy");
        const endDateParsed = DateTime.fromISO(endDate).toFormat("dd LLL yyyy");

        return `${startDateParsed} - ${endDateParsed}`;
    } else {
        return "Select Date";
    }
}

export function getDatePickerLabelWithoutCanvasControls(
    existingDateRangeOption: DateRangeOptions | null,
    values: [Iso8601DateTime | null, Iso8601DateTime | null],
) {
    return existingDateRangeOption === null && values[0] !== null && values[1] !== null
        ? `${DateTime.fromISO(values[0]).toFormat("dd LLL yyyy")} - ${DateTime.fromISO(values[1]).toFormat("dd LLL yyyy")}`
        : existingDateRangeOption !== null && dateRangeOptions[existingDateRangeOption] !== null
          ? dateRangeOptions[existingDateRangeOption].humanReadableName
          : "Select Date";
}
